import React, { Component } from "react";
import { withModulesManager, formatMessage } from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Card,
  CardContent,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import {
  fetchFamilyEnrollment,
  updateFamilyEnrollment,
  submitFamilyEnrollment,
} from "../../actions";
import {
  toBackendFamilyPayload,
  normalizeFamilyData,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: {
    margin: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    flex: 1,
  },
  form: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  formCard: {
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  addMemberButton: {
    marginBottom: theme.spacing(2),
  },
});

class FamilyEnrollmentEdit extends Component {
  state = {
    enrollment: {
      familyId: "",
      headOfFamily: "",
      headChfId: "",
      location: "",
      phone: "",
      email: "",
      status: "Active",
      enrollmentDate: "",
      validityFrom: "",
      validityTo: "",
      policyValue: "",
      policyNumber: "",
      premiumAmount: "",
      members: [],
    },
    errors: {},
    isLoading: false,
  };

  componentDidMount() {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid && this.props.fetchFamilyEnrollment) {
      this.props.fetchFamilyEnrollment(this.props.modulesManager, uuid);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.familyEnrollment &&
      this.props.familyEnrollment !== prevProps.familyEnrollment
    ) {
      const e = this.props.familyEnrollment;
      const d = normalizeFamilyData(e.data || e.familyData);
      this.setState({
        enrollment: {
          uuid: e.uuid,
          familyId: e.uuid || "",
          headOfFamily: [d.headLastName, d.headOtherNames].filter(Boolean).join(" "),
          headChfId: d.headChfId || "",
          location: d.locationId || "",
          phone: d.headPhone || "",
          email: "",
          status: e.status || "DRAFT",
          enrollmentDate: (e.dateCreated || "").toString().slice(0, 10),
          validityFrom: "",
          validityTo: "",
          policyValue: "",
          policyNumber: "",
          premiumAmount: "",
          notes: e.notes || "",
          members: [],
          poverty: d.poverty,
          familyType: d.familyType,
          address: d.address || "",
          confirmationNo: d.confirmationNo,
          confirmationType: d.confirmationType,
          ethnicity: d.ethnicity,
          headLastName: d.headLastName || "",
          headOtherNames: d.headOtherNames || "",
          headDob: (d.headDob || "").toString().slice(0, 10),
          headGender: d.headGender || "",
        },
      });
    }
  }

  handleInputChange = (field) => (event) => {
    const value = event.target.value;
    this.setState({
      enrollment: {
        ...this.state.enrollment,
        [field]: value,
      },
      errors: {
        ...this.state.errors,
        [field]: "",
      },
    });
  };

  handleMemberChange = (memberIndex, field) => (event) => {
    const value = event.target.value;
    const members = [...this.state.enrollment.members];
    members[memberIndex] = {
      ...members[memberIndex],
      [field]: value,
    };

    this.setState({
      enrollment: {
        ...this.state.enrollment,
        members,
      },
    });
  };

  addMember = () => {
    const newMember = {
      id: Date.now(),
      name: "",
      relationship: "",
      chfId: "",
      age: "",
      gender: "",
    };

    this.setState({
      enrollment: {
        ...this.state.enrollment,
        members: [...this.state.enrollment.members, newMember],
      },
    });
  };

  removeMember = (memberIndex) => {
    const members = this.state.enrollment.members.filter(
      (_, index) => index !== memberIndex
    );
    this.setState({
      enrollment: {
        ...this.state.enrollment,
        members,
      },
    });
  };

  validateForm = () => {
    const { enrollment } = this.state;
    const errors = {};

    if (!enrollment.familyId.trim()) {
      errors.familyId = "Family ID is required";
    }
    if (!enrollment.headOfFamily.trim()) {
      errors.headOfFamily = "Head of Family is required";
    }
    if (!enrollment.headChfId.trim()) {
      errors.headChfId = "Head CHF ID is required";
    }
    if (!enrollment.enrollmentDate) {
      errors.enrollmentDate = "Enrollment date is required";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSave = () => {
    if (!this.validateForm()) return;
    const { enrollment } = this.state;
    const uuid = enrollment?.uuid || this.props.familyEnrollment?.uuid;
    if (!uuid) return;

    this.setState({ isLoading: true });
    const payload = {
      uuid,
      familyData: toBackendFamilyPayload(enrollment),
      notes: enrollment.notes,
    };
    Promise.resolve(
      this.props.updateFamilyEnrollment(
        this.props.modulesManager,
        payload,
        "Update Family Enrollment"
      )
    )
      .then(() => {
        this.setState({ isLoading: false });
        this.handleBack();
      })
      .catch(() => this.setState({ isLoading: false }));
  };

  handleCancel = () => {
    this.handleBack();
  };

  handleBack = () => {
    const uuid =
      this.state.enrollment?.uuid || this.props.familyEnrollment?.uuid;
    if (this.props.history && uuid) {
      this.props.history.push(`/enrollment/family/${uuid}/detail`);
    } else {
      window.history.back();
    }
  };

  render() {
    const { classes, intl } = this.props;
    const { enrollment, errors, isLoading } = this.state;

    return (
      <Box className={classes.page}>
        <Card style={{ margin: 16 }}>
          <CardContent style={{ padding: 24 }}>
            <Box className={classes.header}>
              <Box display="flex" alignItems="center" flex={1}>
                <IconButton
                  className={classes.backButton}
                  onClick={this.handleBack}
                  color="primary"
                >
                  <BackIcon />
                </IconButton>
                <Typography variant="h4" className={classes.title}>
                  {formatMessage(
                    intl,
                    "enrollment",
                    "familyEnrollment.edit.title"
                  )}
                </Typography>
              </Box>
            </Box>

            <form className={classes.form}>
              <Grid container spacing={3}>
                {/* Family Information Section */}
                <Grid item xs={12} md={6}>
                  <Card className={classes.formCard}>
                    <CardContent>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {formatMessage(
                          intl,
                          "enrollment",
                          "familyEnrollment.edit.familyInfo"
                        )}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.familyId"
                            )}
                            value={enrollment.familyId}
                            onChange={this.handleInputChange("familyId")}
                            error={!!errors.familyId}
                            helperText={errors.familyId}
                            required
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.headOfFamily"
                            )}
                            value={enrollment.headOfFamily}
                            onChange={this.handleInputChange("headOfFamily")}
                            error={!!errors.headOfFamily}
                            helperText={errors.headOfFamily}
                            required
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.edit.headChfId"
                            )}
                            value={enrollment.headChfId}
                            onChange={this.handleInputChange("headChfId")}
                            error={!!errors.headChfId}
                            helperText={errors.headChfId}
                            required
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.location"
                            )}
                            value={enrollment.location}
                            onChange={this.handleInputChange("location")}
                            multiline
                            minRows={2}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.phone"
                            )}
                            value={enrollment.phone}
                            onChange={this.handleInputChange("phone")}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.edit.email"
                            )}
                            value={enrollment.email}
                            onChange={this.handleInputChange("email")}
                            type="email"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Enrollment Information Section */}
                <Grid item xs={12} md={6}>
                  <Card className={classes.formCard}>
                    <CardContent>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        {formatMessage(
                          intl,
                          "enrollment",
                          "familyEnrollment.edit.enrollmentInfo"
                        )}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>
                              {formatMessage(
                                intl,
                                "enrollment",
                                "familyEnrollment.status"
                              )}
                            </InputLabel>
                            <Select
                              value={enrollment.status}
                              onChange={this.handleInputChange("status")}
                            >
                              <MenuItem value="Active">Active</MenuItem>
                              <MenuItem value="Inactive">Inactive</MenuItem>
                              <MenuItem value="Pending">Pending</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.enrollmentDate"
                            )}
                            type="date"
                            value={enrollment.enrollmentDate}
                            onChange={this.handleInputChange("enrollmentDate")}
                            error={!!errors.enrollmentDate}
                            helperText={errors.enrollmentDate}
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.edit.validityFrom"
                            )}
                            type="date"
                            value={enrollment.validityFrom}
                            onChange={this.handleInputChange("validityFrom")}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.edit.validityTo"
                            )}
                            type="date"
                            value={enrollment.validityTo}
                            onChange={this.handleInputChange("validityTo")}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.edit.policyNumber"
                            )}
                            value={enrollment.policyNumber}
                            onChange={this.handleInputChange("policyNumber")}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.policyValue"
                            )}
                            value={enrollment.policyValue}
                            onChange={this.handleInputChange("policyValue")}
                            type="number"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={formatMessage(
                              intl,
                              "enrollment",
                              "familyEnrollment.edit.premiumAmount"
                            )}
                            value={enrollment.premiumAmount}
                            onChange={this.handleInputChange("premiumAmount")}
                            type="number"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Family Members Section */}
                <Grid item xs={12}>
                  <Card className={classes.formCard}>
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography
                          variant="h6"
                          className={classes.sectionTitle}
                        >
                          {formatMessage(
                            intl,
                            "enrollment",
                            "familyEnrollment.edit.familyMembers"
                          )}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={this.addMember}
                          className={classes.addMemberButton}
                        >
                          {formatMessage(
                            intl,
                            "enrollment",
                            "familyEnrollment.edit.addMember"
                          )}
                        </Button>
                      </Box>

                      <List>
                        {enrollment.members.map((member, index) => (
                          <React.Fragment key={member.id}>
                            <ListItem>
                              <ListItemText
                                primary={
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                      <TextField
                                        fullWidth
                                        label="Name"
                                        value={member.name}
                                        onChange={this.handleMemberChange(
                                          index,
                                          "name"
                                        )}
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                      <TextField
                                        fullWidth
                                        label="CHF ID"
                                        value={member.chfId}
                                        onChange={this.handleMemberChange(
                                          index,
                                          "chfId"
                                        )}
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                      <TextField
                                        fullWidth
                                        label="Age"
                                        value={member.age}
                                        onChange={this.handleMemberChange(
                                          index,
                                          "age"
                                        )}
                                        type="number"
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Gender</InputLabel>
                                        <Select
                                          value={member.gender}
                                          onChange={this.handleMemberChange(
                                            index,
                                            "gender"
                                          )}
                                        >
                                          <MenuItem value="M">Male</MenuItem>
                                          <MenuItem value="F">Female</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={1}>
                                      <IconButton
                                        color="secondary"
                                        onClick={() => this.removeMember(index)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                }
                              />
                            </ListItem>
                            {index < enrollment.members.length - 1 && (
                              <Divider />
                            )}
                          </React.Fragment>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box className={classes.buttonContainer}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={this.handleCancel}
                  disabled={isLoading}
                >
                  {formatMessage(
                    intl,
                    "enrollment",
                    "familyEnrollment.edit.cancel"
                  )}
                </Button>
                <Button
                  variant="contained"
                  className={classes.saveButton}
                  startIcon={<SaveIcon />}
                  onClick={this.handleSave}
                  disabled={isLoading}
                >
                  {isLoading
                    ? formatMessage(
                        intl,
                        "enrollment",
                        "familyEnrollment.edit.saving"
                      )
                    : formatMessage(
                        intl,
                        "enrollment",
                        "familyEnrollment.edit.save"
                      )}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  familyEnrollment: state.enrollment?.familyEnrollmentDetail || null,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchFamilyEnrollment,
      updateFamilyEnrollment,
      submitFamilyEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withStyles(styles)(
    injectIntl(
      connect(mapStateToProps, mapDispatchToProps)(FamilyEnrollmentEdit)
    )
  )
);
